import { useAppSelector, useAppDispatch } from '../../store/store';
import { 
  fetchConversations, 
  setCurrentConversation, 
  sendMessage,
  fetchMessages,
  fetchOlderMessages,
  markAsRead,
  addOptimisticMessage,
  removeOptimisticMessage,
  updateMessageId,
  updateConversationLastMessage,
  unblockUser,
  fetchBlockedUsers
} from '../../store/slices/chatSlice';
import { fetchAllInterests, acceptInterest, declineInterest } from '../../store/slices/activitySlice';
import { markNotificationsBySenderAsRead } from '../../store/slices/notificationSlice';
import { toast } from 'sonner';
import axios from 'axios';
import { useCheckBlockStatus } from '../../hooks/useInteractionQueries';
import { API_URL } from '../../lib/api-client';
import { useQueryClient } from '@tanstack/react-query';
import { generateProfileSlug } from '../../utils/seo';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { useNavigate } from 'react-router-dom';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ArrowLeft, Ban, CheckCircle2, CreditCard, Flag, Heart, Lock, Mail, MoreVertical, Plus, Search, Send, Shield, Smile, User, Video } from 'lucide-react';
import { ImageWithFallback } from '../figma/ImageWithFallback';
import { ConversationItem } from '../chat/ConversationItem';
import { useWebRTC } from '../WebRTCProvider';
import { useAuthContext } from '../../contexts/AuthContext';
import { logger } from '../../utils/logger';
import { ReportUserModal } from '../ReportUserModal';

interface MessagesTabBeautifulProps {
  onNavigateToPlans?: () => void;
}

export function MessagesTabBeautiful({ onNavigateToPlans }: MessagesTabBeautifulProps = {}) {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  
  const [searchQuery, setSearchQuery] = useState('');
  const [messageInput, setMessageInput] = useState('');
  const [activeTab, setActiveTab] = useState<'all' | 'received' | 'sent'>('all');
  const [acceptingInterest, setAcceptingInterest] = useState<string | null>(null);
  const [decliningInterest, setDecliningInterest] = useState<string | null>(null);
  const [reportingUser, setReportingUser] = useState(false);
  const [blockingUser, setBlockingUser] = useState(false);
  const [videoCallEnabled, setVideoCallEnabled] = useState(true);
  const [isUpdatingVideoCallPref, setIsUpdatingVideoCallPref] = useState(false);
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const [userToReport, setUserToReport] = useState<{id: string, name: string} | null>(null);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  
  // Separate interests state (NOT from conversations)
  const [receivedInterests, setReceivedInterests] = useState<any[]>([]);
  const [sentInterests, setSentInterests] = useState<any[]>([]);
  const [interestsLoading, setInterestsLoading] = useState(false);
  
  // Selected interest for viewing details
  const [selectedInterest, setSelectedInterest] = useState<any | null>(null);
  const [hasRestoredConversation, setHasRestoredConversation] = useState(false); // Track if we've restored conversation
  const [unblocking, setUnblocking] = useState(false); // Track unblock action
  
  // Ref for auto-scrolling to latest message
  const messagesEndRef = useRef<HTMLDivElement>(null);
  // Ref for messages container to detect scroll
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  // Track the last conversation ID to detect when conversation changes
  const lastConversationIdRef = useRef<string | null>(null);
  // Track if we're loading older messages to prevent auto-scroll
  const isLoadingOlderRef = useRef<boolean>(false);
  // Track the last loaded page to prevent duplicate loads
  const lastLoadedPageRef = useRef<number>(1);
  // Track user manual scrolling to prevent auto-scroll interference
  const userScrollingRef = useRef<boolean>(false);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  // Track last load time to prevent rapid successive loads
  const lastLoadTimeRef = useRef<number>(0);

  const { 
    conversations, 
    currentConversation, 
    messages, 
    loading, 
    messagesLoading,
    loadingOlderMessages,
    hasMoreMessages,
    messagesPage,
    onlineUsers,
    userPresence,
    cacheValid,
    blockedUsers
  } = useAppSelector(state => state.chat);

  const { user, loading: authLoading } = useAppSelector(state => state.auth);
  const { profile } = useAppSelector(state => state.profile);
  const { webrtcService } = useWebRTC();
  const { user: authUser } = useAuthContext();

  // Check if user has active subscription
  const hasActiveSubscription = useMemo(() => {
    return user?.subscription?.planId && user?.subscription?.isActive;
  }, [user?.subscription]);

  // Fetch conversations on mount - only if cache is invalid
  useEffect(() => {
    const token = localStorage.getItem('token');
    
    logger.log('🔄 [MessagesTab] Conversations fetch effect triggered', {
      cacheValid,
      hasUser: !!user,
      hasToken: !!token,
      conversationsCount: conversations.length,
      loading
    });
    
    // Fetch if: token exists + cache invalid + not already loading + no existing conversations from cache
    if (token && !cacheValid && !loading) {
      logger.log('📡 [MessagesTab] Dispatching fetchConversations...');
      dispatch(fetchConversations());
      // Also fetch blocked users
      dispatch(fetchBlockedUsers());
    } else if (!token) {
      logger.warn('⚠️ [MessagesTab] No token found, skipping fetch');
    } else if (cacheValid) {
      logger.log('✅ [MessagesTab] Using cached conversations:', conversations.length);
    } else if (loading) {
      logger.log('⏳ [MessagesTab] Already loading conversations...');
    }
  }, [dispatch, cacheValid, loading, conversations.length, user]);

  // Restore last conversation on page refresh (only once)
  useEffect(() => {
    if (conversations.length > 0 && !currentConversation && !hasRestoredConversation) {
      try {
        const lastConversationId = localStorage.getItem('lastConversationId');
        if (lastConversationId) {
          const conversation = conversations.find(c => c._id === lastConversationId);
          if (conversation) {
            logger.log('🔄 [MessagesTab] Restoring last conversation:', lastConversationId);
            dispatch(setCurrentConversation(conversation));
          }
        }
      } catch (e) {
        logger.error('Failed to restore conversation:', e);
      } finally {
        // Mark as restored even if we didn't find a conversation (to prevent retrying)
        setHasRestoredConversation(true);
      }
    }
  }, [conversations, currentConversation, hasRestoredConversation, dispatch]);

  // Fetch interests separately
  useEffect(() => {
    const fetchInterests = async () => {
      const token = localStorage.getItem('token');
      if (!token) return;

      setInterestsLoading(true);
      try {
        // Fetch received pending interests - SAME API AS ACTIVITY TAB
        const receivedResponse = await axios.get(
          `${API_URL}/interactions/received?type=interest,superlike&status=pending`,
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );
        if (process.env.NODE_ENV === 'development') {
          console.log('📥 [RAW API] Received Response:', receivedResponse.data);
        }
        if (receivedResponse.data.success) {
          setReceivedInterests(receivedResponse.data.data.interactions);
          if (process.env.NODE_ENV === 'development') {
            console.log('📥 Received pending interests:', receivedResponse.data.data.interactions.length, receivedResponse.data.data.interactions);
          }
        }

        // Fetch sent pending interests - SAME API AS ACTIVITY TAB
        const sentResponse = await axios.get(
          `${API_URL}/interactions/sent?type=interest,superlike&status=pending`,
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );
        if (process.env.NODE_ENV === 'development') {
          console.log('📤 [RAW API] Sent Response:', sentResponse.data);
        }
        if (sentResponse.data.success) {
          // Filter out any interests that are not actually pending, or already have a conversation
          const pendingInterests = sentResponse.data.data.interactions.filter((interest: any) => {
            // Only keep truly pending interests
            if (interest.status !== 'pending') {
              if (process.env.NODE_ENV === 'development') {
                console.log('🚫 Filtering out non-pending interest:', interest._id, interest.status);
              }
              return false;
            }
            
            // Check if a conversation already exists for this interest (meaning it was accepted)
            const hasConversation = conversations.some((conv: any) => 
              conv.interestId === interest._id || 
              (conv.participants?.some((p: any) => p._id === interest.to || p._id === interest.toUserId))
            );
            
            if (hasConversation) {
              if (process.env.NODE_ENV === 'development') {
                console.log('🚫 Filtering out interest with existing conversation:', interest._id);
              }
              return false;
            }
            
            return true;
          });
          
          setSentInterests(pendingInterests);
          if (process.env.NODE_ENV === 'development') {
            console.log('📤 Sent pending interests (filtered):', pendingInterests.length, pendingInterests);
          }
        }
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.error('Error fetching interests:', error);
        }
      } finally {
        setInterestsLoading(false);
      }
    };

    fetchInterests();
    
    // Refresh interests every 30 seconds (optimized from 10s to reduce server load)
    const interval = setInterval(fetchInterests, 30000);
    return () => clearInterval(interval);
  }, []); // Only run once on mount and every 30s - removed conversations dependency to prevent continuous re-fetching

  const getOtherParticipant = useCallback((conversation: any) => {
    if (!conversation || !conversation.participants) return null;
    return conversation.participants.find((p: any) => p._id !== user?._id);
  }, [user?._id]);

  // Memoized value for the current conversation's other participant with comprehensive fallback
  const currentOtherParticipant = useMemo(() => {
    if (!currentConversation) {
      logger.log('📞 [currentOtherParticipant] No current conversation');
      return null;
    }
    
    logger.log('📞 [currentOtherParticipant] Finding other participant for conversation:', currentConversation._id);
    
    // First try the standard way
    let otherUser = getOtherParticipant(currentConversation);
    logger.log('📞 [currentOtherParticipant] Standard method result:', otherUser?._id);
    
    // If that fails, try to find the full conversation in the list
    if (!otherUser?._id && currentConversation?._id) {
      logger.log('📞 [currentOtherParticipant] Searching in conversations list...');
      const fullConversation = conversations.find(c => c._id === currentConversation._id);
      if (fullConversation) {
        logger.log('📞 [currentOtherParticipant] Found full conversation');
        otherUser = getOtherParticipant(fullConversation);
        logger.log('📞 [currentOtherParticipant] Full conversation method result:', otherUser?._id);
      }
    }
    
    // Additional fallback: try to extract user from various possible structures
    if (!otherUser?._id) {
      logger.log('📞 [currentOtherParticipant] Trying fallback methods...');
      
      // Try participants array directly
      if (currentConversation.participants && Array.isArray(currentConversation.participants)) {
        logger.log('📞 [currentOtherParticipant] Checking participants array:', currentConversation.participants.length);
        otherUser = currentConversation.participants.find((p: any) => 
          (p._id || p) !== user?._id
        );
        if (otherUser?._id) logger.log('📞 [currentOtherParticipant] Found via participants array:', otherUser._id);
      }
      
      // Try user1/user2 structure
      if (!otherUser?._id) {
        logger.log('📞 [currentOtherParticipant] Checking user1/user2 structure');
        if (currentConversation.user1?._id && currentConversation.user1._id !== user?._id) {
          otherUser = currentConversation.user1;
          logger.log('📞 [currentOtherParticipant] Found via user1:', otherUser._id);
        } else if (currentConversation.user2?._id && currentConversation.user2._id !== user?._id) {
          otherUser = currentConversation.user2;
          logger.log('📞 [currentOtherParticipant] Found via user2:', otherUser._id);
        }
      }
      
      // Try lastMessage sender
      if (!otherUser?._id && currentConversation.lastMessage?.sender?._id && currentConversation.lastMessage.sender._id !== user?._id) {
        otherUser = currentConversation.lastMessage.sender;
        logger.log('📞 [currentOtherParticipant] Found via lastMessage sender:', otherUser._id);
      }
    }
    
    logger.log('📞 [currentOtherParticipant] Final result:', otherUser?._id || 'NOT FOUND');
    return otherUser;
  }, [currentConversation, conversations, getOtherParticipant, user?._id]);

  // Check blocking status
  const otherUserId = currentOtherParticipant?._id || currentOtherParticipant;
  
  // Use the API to check if they blocked me
  const { data: blockStatusData } = useCheckBlockStatus(otherUserId);
  
  const iBlockedThem = useMemo(() => {
    return blockedUsers.includes(otherUserId);
  }, [blockedUsers, otherUserId]);

  const theyBlockedMe = useMemo(() => {
    // Use the API response to check if they blocked me
    logger.log('🚫 [Block Check] Block status data:', blockStatusData);
    logger.log('🚫 [Block Check] Other user ID:', otherUserId);
    // Handle both response structures: direct fields or nested in data
    const isBlocked = blockStatusData?.isBlockedByThem || blockStatusData?.data?.isBlockedByThem || false;
    logger.log('🚫 [Block Check] They blocked me:', isBlocked);
    return isBlocked;
  }, [blockStatusData, otherUserId]);

  const isBlocked = iBlockedThem || theyBlockedMe;

  const filteredConversations = useMemo(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log('🔍 [Filtering] Active Tab:', activeTab);
      console.log('🔍 [Filtering] Total conversations:', conversations.length);
    }
    
    let filtered = conversations;

    // Filter by tab
    if (activeTab === 'all') {
      // All Messages: Show conversations that are accepted OR don't have interest status (regular chats)
      filtered = conversations.filter((conv: any) => {
        // If no interestStatus field, it's a regular chat - show it
        if (!conv.interestStatus) {
          if (process.env.NODE_ENV === 'development') {
            console.log('✅ [All] Regular chat (no interestStatus):', conv._id);
          }
          return true;
        }
        // If interestStatus is 'accepted', show it
        if (conv.interestStatus === 'accepted') {
          if (process.env.NODE_ENV === 'development') {
            console.log('✅ [All] Accepted interest:', conv._id);
          }
          return true;
        }
        // Otherwise don't show
        if (process.env.NODE_ENV === 'development') {
          console.log('❌ [All] Filtered out:', conv._id, conv.interestStatus);
        }
        return false;
      });
    } else if (activeTab === 'received') {
      // Received: Show interests I RECEIVED (pending, waiting for me to accept/decline)
      filtered = conversations.filter((conv: any) => {
        const isPending = conv.interestStatus === 'pending';
        const iAmRecipient = conv.interestRecipient === user?._id;
        
        if (process.env.NODE_ENV === 'development') {
          console.log('🔍 [Received] Checking:', {
            id: conv._id,
            interestStatus: conv.interestStatus,
            interestRecipient: conv.interestRecipient,
            myId: user?._id,
            isPending,
            iAmRecipient,
            willShow: isPending && iAmRecipient
          });
        }
        
        return isPending && iAmRecipient;
      });
    } else if (activeTab === 'sent') {
      // Sent: Show interests I SENT (pending, waiting for other person's response)
      filtered = conversations.filter((conv: any) => {
        const isPending = conv.interestStatus === 'pending';
        const iAmSender = conv.interestSender === user?._id;
        
        if (process.env.NODE_ENV === 'development') {
          console.log('🔍 [Sent] Checking:', {
            id: conv._id,
            interestStatus: conv.interestStatus,
            interestSender: conv.interestSender,
            myId: user?._id,
            isPending,
            iAmSender,
            willShow: isPending && iAmSender
          });
        }
        
        return isPending && iAmSender;
      });
    }

    if (process.env.NODE_ENV === 'development') {
      console.log('📊 [Filtering] Filtered count:', filtered.length);
    }

    // Sort by updatedAt (most recent first) - do this BEFORE search filter
    filtered = filtered.sort((a: any, b: any) => {
      const aTime = a.updatedAt ? new Date(a.updatedAt).getTime() : 0;
      const bTime = b.updatedAt ? new Date(b.updatedAt).getTime() : 0;
      return bTime - aTime; // Descending order (newest first)
    });

    // Apply search filter
    if (!searchQuery.trim()) return filtered;
    
    const query = searchQuery.toLowerCase();
    return filtered.filter((conv: any) => {
      const otherParticipant = getOtherParticipant(conv);
      if (!otherParticipant) return false;
      
      const fullName = `${otherParticipant.firstName || ''} ${otherParticipant.lastName || ''}`.toLowerCase();
      return fullName.includes(query);
    });
  }, [conversations, searchQuery, getOtherParticipant, activeTab, user?._id]);

  // Check if current pending conversation was accepted - switch to real conversation
  useEffect(() => {
    if (currentConversation?.isPending && currentConversation?.interestId) {
      // Check if a real conversation now exists for this interest
      const realConversation = conversations.find((conv: any) => 
        conv.interestId === currentConversation.interestId && !conv.isPending
      );
      
      if (realConversation) {
        if (process.env.NODE_ENV === 'development') {
          console.log('✅ Interest was accepted! Switching to real conversation:', realConversation._id);
        }
        dispatch(setCurrentConversation(realConversation));
        setActiveTab('all'); // Switch to All tab
        toast.success('Your interest was accepted! You can now chat.');
        return;
      }

      // Check if the sent interest still exists (not accepted/declined yet)
      const stillPending = sentInterests.find((interest: any) => interest._id === currentConversation.interestId);
      
      if (!stillPending) {
        // Interest was accepted or declined - close the pending view
        if (process.env.NODE_ENV === 'development') {
          console.log('❌ Sent interest no longer pending, closing view');
        }
        dispatch(setCurrentConversation(null));
      }
    }
  }, [conversations, currentConversation, dispatch, sentInterests]);

  // Remove sent interests that now have conversations (accepted interests)
  useEffect(() => {
    if (sentInterests.length > 0 && conversations.length > 0) {
      const filteredSent = sentInterests.filter((interest: any) => {
        const hasConversation = conversations.some((conv: any) => 
          conv.interestId === interest._id
        );
        if (hasConversation && process.env.NODE_ENV === 'development') {
          console.log('🗑️ Removing accepted sent interest from UI:', interest._id);
        }
        return !hasConversation;
      });
      
      if (filteredSent.length !== sentInterests.length) {
        setSentInterests(filteredSent);
      }
    }
  }, [conversations, sentInterests]);

  const handleSelectConversation = useCallback((conversation: any) => {
    dispatch(setCurrentConversation(conversation));
  }, [dispatch]);

  // Scroll to bottom of messages
  const scrollToBottom = useCallback((instant = false) => {
    messagesEndRef.current?.scrollIntoView({ behavior: instant ? 'auto' : 'smooth' });
  }, []);

  // Load older messages
  const handleLoadMore = useCallback(async () => {
    if (loadingOlderMessages || !hasMoreMessages || !currentConversation?._id) return;
    
    const nextPage = messagesPage + 1;
    const now = Date.now();
    
    // Prevent rapid successive loads (minimum 1 second between loads)
    if (now - lastLoadTimeRef.current < 1000) {
      logger.log('⏱️ Throttling: Too soon since last load');
      return;
    }
    
    // Prevent loading the same page multiple times
    if (lastLoadedPageRef.current >= nextPage) {
      logger.log('⏭️ Skipping duplicate page load:', nextPage);
      return;
    }
    
    logger.log('📜 Loading older messages - page:', nextPage);
    
    // Set flag to prevent auto-scroll and mark page as loading
    isLoadingOlderRef.current = true;
    lastLoadedPageRef.current = nextPage;
    lastLoadTimeRef.current = now;
    
    // Save current scroll position
    const container = messagesContainerRef.current;
    const scrollHeightBefore = container?.scrollHeight || 0;
    
    await dispatch(fetchOlderMessages({ 
      conversationId: currentConversation._id, 
      page: nextPage, 
      limit: 50 
    }));
    
    // Restore scroll position after loading older messages
    setTimeout(() => {
      if (container) {
        const scrollHeightAfter = container.scrollHeight;
        container.scrollTop = scrollHeightAfter - scrollHeightBefore;
      }
      // Reset flag after scroll position is restored
      isLoadingOlderRef.current = false;
    }, 100);
  }, [loadingOlderMessages, hasMoreMessages, currentConversation, messagesPage, dispatch]);

  // Detect scroll to top for loading older messages (with debounce)
  const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const container = e.currentTarget;
    
    // Mark that user is manually scrolling
    userScrollingRef.current = true;
    
    // Clear existing timeout
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }
    
    // Reset user scrolling flag after 500ms of no scroll activity
    scrollTimeoutRef.current = setTimeout(() => {
      userScrollingRef.current = false;
    }, 500);
    
    // Don't trigger if already loading
    if (isLoadingOlderRef.current || loadingOlderMessages) return;
    
    // Load more when scrolled within 100px of top AND there are more messages
    if (container.scrollTop < 100 && hasMoreMessages) {
      handleLoadMore();
    }
  }, [hasMoreMessages, loadingOlderMessages, handleLoadMore]);

  // Fetch messages when a conversation is selected or on page refresh
  useEffect(() => {
    if (currentConversation?._id && !currentConversation.isPending) {
      logger.log('📨 [MessagesTab] Fetching messages for conversation:', currentConversation._id);
      
      // Reset pagination state when switching conversations
      lastLoadedPageRef.current = 1;
      isLoadingOlderRef.current = false;
      lastLoadTimeRef.current = 0;
      userScrollingRef.current = false;
      
      // Fetch last 50 messages (page 1)
      dispatch(fetchMessages({ conversationId: currentConversation._id, page: 1, limit: 50 }));
      // Mark as read when opening conversation
      dispatch(markAsRead(currentConversation._id));
    }
  }, [currentConversation?._id, dispatch]);

  // Instant scroll when conversation first opens or changes
  useEffect(() => {
    if (currentConversation && messages.length > 0) {
      const conversationChanged = lastConversationIdRef.current !== currentConversation._id;
      
      if (conversationChanged) {
        lastConversationIdRef.current = currentConversation._id;
        // Instant scroll when opening a conversation
        setTimeout(() => {
          scrollToBottom(true);
        }, 150);
      }
    }
  }, [currentConversation?._id, messages.length, scrollToBottom]);

  // Auto-scroll to bottom when messages change or new message arrives (smooth)
  // BUT only if user is already at the bottom (not scrolled up reading old messages)
  useEffect(() => {
    // Don't auto-scroll if we're loading older messages
    if (isLoadingOlderRef.current) return;
    
    // Don't auto-scroll if user is actively scrolling
    if (userScrollingRef.current) return;
    
    if (messages.length > 0 && currentConversation && !loadingOlderMessages) {
      const container = messagesContainerRef.current;
      if (!container) return;
      
      // Check if user is near the bottom (within 200px)
      const isNearBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 200;
      
      // Only auto-scroll if user is near the bottom
      if (isNearBottom) {
        setTimeout(() => {
          scrollToBottom(false);
        }, 100);
      }
    }
  }, [messages.length, currentConversation, loadingOlderMessages, scrollToBottom]);

  // Cleanup scroll timeout on unmount
  useEffect(() => {
    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, []);

  const handleSendMessage = async () => {
    if (!messageInput.trim() || !currentConversation) return;

    // Prevent sending if blocked
    if (isBlocked) {
      if (iBlockedThem) {
        toast.error('Unblock this user to send messages');
      } else {
        toast.error('You cannot send messages to this user');
      }
      return;
    }

    if (!hasActiveSubscription) {
      const currentUserId = user?._id;
      
      if (currentConversation.chatRestriction?.restrictedUserId === currentUserId) {
        toast.error(currentConversation.chatRestriction.reason || 'You need a premium subscription to reply in this chat.');
        return;
      }
      
      if (currentConversation.initiatedBy === currentUserId) {
        toast.error('You need a premium subscription to send messages.');
        return;
      }
    }

    const messageData = {
      conversationId: currentConversation._id,
      content: messageInput.trim()
    };

    const messageCopy = messageInput;
    
    const tempId = `temp_${Date.now()}_${Math.random()}`;
    const optimisticMessage = {
      _id: tempId,
      senderId: user?._id || '',
      content: messageInput.trim(),
      messageType: 'text',
      isRead: false,
      createdAt: new Date().toISOString()
    };
    
    setMessageInput('');
    dispatch(addOptimisticMessage(optimisticMessage));
    dispatch(updateConversationLastMessage({
      conversationId: currentConversation._id,
      message: optimisticMessage
    }));
    
    // Scroll to bottom immediately after sending (force scroll, not smooth)
    setTimeout(() => scrollToBottom(false), 50);

    try {
      const result = await dispatch(sendMessage(messageData)).unwrap();
      
      if (result && result._id) {
        dispatch(updateMessageId({
          tempId: tempId,
          realId: result._id,
          message: result
        }));
      }
    } catch (error: any) {
      dispatch(removeOptimisticMessage(tempId));
      setMessageInput(messageCopy);
      
      const errorMessage = error.message || error || 'Failed to send message';
      if (errorMessage.includes('Conversation not found') || errorMessage.includes('not found')) {
        toast.error('Conversation not found. Please refresh and try again.');
        dispatch(fetchConversations());
      } else {
        toast.error(errorMessage);
      }
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Common emojis for quick access
  const commonEmojis = ['😊', '😂', '❤️', '👍', '🙏', '😍', '🎉', '😢', '🔥', '💯', '✨', '🌹', '💕', '😘', '🥰', '😁', '👏', '🙌', '💪', '🤗'];

  const handleEmojiSelect = (emoji: string) => {
    setMessageInput(prev => prev + emoji);
    setShowEmojiPicker(false);
  };

  const formatMessageTime = useCallback((timestamp: string | Date) => {
    try {
      const date = new Date(timestamp);
      if (isNaN(date.getTime())) return '';
      
      const now = new Date();
      const isToday = date.toDateString() === now.toDateString();
      const yesterday = new Date(now);
      yesterday.setDate(yesterday.getDate() - 1);
      const isYesterday = date.toDateString() === yesterday.toDateString();
      
      if (isToday) {
        return date.toLocaleTimeString('en-US', { 
          hour: '2-digit', 
          minute: '2-digit',
          hour12: true 
        });
      } else if (isYesterday) {
        return 'Yesterday';
      } else {
        return date.toLocaleDateString('en-US', { 
          month: 'short', 
          day: 'numeric' 
        });
      }
    } catch {
      return '';
    }
  }, []);

  const formatFullMessageTime = (timestamp: string | Date) => {
    try {
      const date = new Date(timestamp);
      if (isNaN(date.getTime())) return '';
      
      return date.toLocaleTimeString('en-US', { 
        hour: '2-digit', 
        minute: '2-digit',
        hour12: true 
      });
    } catch {
      return '';
    }
  };

  const handleAcceptInterest = async (interestId: string, interest: any) => {
    setAcceptingInterest(interestId);
    try {
      await dispatch(acceptInterest({ interestId, interest })).unwrap();
      toast.success('Interest accepted! You can now chat.');
      // Don't call fetchConversations - it will trigger loading state
      // The socket will update conversations in real-time
      dispatch(fetchAllInterests());
      // Remove from received interests
      setReceivedInterests(prev => prev.filter(i => i._id !== interestId));
      setSelectedInterest(null);
    } catch (error: any) {
      toast.error(error || 'Failed to accept interest.');
    } finally {
      setAcceptingInterest(null);
    }
  };

  const handleDeclineInterest = async (interestId: string) => {
    setDecliningInterest(interestId);
    try {
      await dispatch(declineInterest({ interestId, filterType: 'received' })).unwrap();
      toast.success('Interest declined!');
      dispatch(fetchAllInterests());
      // Remove from received interests
      setReceivedInterests(prev => prev.filter(i => i._id !== interestId));
      setSelectedInterest(null);
    } catch (error: any) {
      toast.error(error || 'Failed to decline interest.');
    } finally {
      setDecliningInterest(null);
    }
  };

  const handleReportUser = async (userId: string, userName: string) => {
    // Open modal instead of directly reporting
    setUserToReport({ id: userId, name: userName });
    setIsReportModalOpen(true);
  };

  const handleSubmitReport = async (reason: string, additionalInfo: string) => {
    if (!userToReport) return;

    setReportingUser(true);
    try {
      const response = await axios.post(`${API_URL}/users/report`, { 
        reportedUserId: userToReport.id,
        reason,
        additionalInfo
      });
      if (response.data.success) {
        toast.success('User reported successfully! Our team will review your report.');
      } else {
        toast.error(response.data.message || 'Failed to report user.');
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || error.message || 'Failed to report user.');
      throw error; // Re-throw to let modal handle it
    } finally {
      setReportingUser(false);
    }
  };

  const handleUnblock = async () => {
    if (!otherUserId) return;
    
    setUnblocking(true);
    try {
      await dispatch(unblockUser(otherUserId)).unwrap();
      // Invalidate the block status query to refresh
      queryClient.invalidateQueries({ queryKey: ['block-status', otherUserId] });
      toast.success('User unblocked successfully!');
    } catch (error: any) {
      toast.error(error || 'Failed to unblock user');
    } finally {
      setUnblocking(false);
    }
  };

  const handleBlockUser = async (userId: string) => {
    setBlockingUser(true);
    try {
      const response = await axios.post(`${API_URL}/users/block`, { blockedUserId: userId });
      if (response.data.success) {
        toast.success('User blocked successfully!');
        // Invalidate block status and fetch updated blocked users list
        queryClient.invalidateQueries({ queryKey: ['block-status', userId] });
        dispatch(fetchBlockedUsers());
        // Don't call fetchConversations - just close the current chat
        dispatch(setCurrentConversation(null));
      } else {
        toast.error(response.data.message || 'Failed to block user.');
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || error.message || 'Failed to block user.');
    } finally {
      setBlockingUser(false);
    }
  };

  // Auto-mark notifications as read when opening a chat
  useEffect(() => {
    if (currentConversation && otherUserId) {
      // Mark all message notifications from this user as read
      dispatch(markNotificationsBySenderAsRead({ 
        senderId: otherUserId, 
        type: 'message' 
      }));
    }
  }, [currentConversation, otherUserId, dispatch]);

  // Update video call permission when conversation changes
  useEffect(() => {
    if (currentConversation?.videoCallPermissions && user?._id) {
      const permission = currentConversation.videoCallPermissions[user._id];
      setVideoCallEnabled(permission !== false); // Default to true if not set
    } else {
      setVideoCallEnabled(true); // Default to enabled
    }
  }, [currentConversation, user?._id]);

  const handleToggleVideoCallPermission = async () => {
    if (!currentConversation?._id || isUpdatingVideoCallPref) return;
    
    setIsUpdatingVideoCallPref(true);
    const newValue = !videoCallEnabled;
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/chats/${currentConversation._id}/video-call-permission`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ enabled: newValue })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setVideoCallEnabled(newValue);
        toast.success(data.message || `Video calls ${newValue ? 'enabled' : 'disabled'}`);
      } else {
        toast.error(data.message || 'Failed to update video call permission');
      }
    } catch (error) {
      toast.error('Failed to update video call permission');
    } finally {
      setIsUpdatingVideoCallPref(false);
    }
  };

  const handleInitiateVideoCall = async () => {
    if (!currentOtherParticipant?._id) {
      logger.error('❌ [Video Call] Cannot find other participant:', {
        hasCurrentConversation: !!currentConversation,
        currentConversationId: currentConversation?._id,
        hasCurrentOtherParticipant: !!currentOtherParticipant
      });
      toast.error('Cannot find user to call');
      return;
    }

    if (!webrtcService) {
      logger.error('❌ [Video Call] WebRTC service not available');
      toast.error('Video call service not available');
      return;
    }

    try {
      const callerName = `${authUser?.firstName || user?.firstName || ''} ${authUser?.lastName || user?.lastName || ''}`.trim();
      const callerPhoto = authUser?.profilePhoto || user?.profilePhoto;
      const roomId = `room-${currentConversation._id}-${Date.now()}`;
      const receiverName = `${currentOtherParticipant.firstName || ''} ${currentOtherParticipant.lastName || ''}`.trim() || 'User';
      
      logger.log('📞 [Video Call] Initiating call:', {
        to: currentOtherParticipant._id,
        toName: receiverName,
        callerName,
        roomId,
        userId: user?._id,
        hasWebrtcService: !!webrtcService
      });
      
      // sendCallRequest handles setting roomId and isVideoCall internally
      webrtcService.sendCallRequest(
        currentOtherParticipant._id,
        callerName || 'User',
        callerPhoto,
        roomId,
        receiverName,
        currentOtherParticipant.profile?.profilePhoto,
        true, // Always video call
        user?._id // Pass userId
      );
      
      logger.log('✅ [Video Call] Call request sent successfully');
      toast.success('Video calling...');
    } catch (error) {
      logger.error('❌ [Video Call] Failed to initiate:', error);
      toast.error('Failed to initiate call');
    }
  };

  const handleViewProfile = (userId: string, firstName?: string, lastName?: string) => {
    const slug = generateProfileSlug(firstName || 'User', lastName || '', userId);
    navigate(`/profile/${slug}`);
  };

  const handleViewInterestProfile = (interest: any) => {
    const profile = interest.fromUserProfile || interest.fromProfile || {};
    const userInfo = interest.fromUser || interest.fromUserId || {};
    const userId = userInfo._id || profile.userId?._id || profile._id;
    const firstName = userInfo.firstName || profile.userId?.firstName || profile.firstName;
    const lastName = userInfo.lastName || profile.userId?.lastName || profile.lastName;
    
    if (userId) {
      handleViewProfile(userId, firstName, lastName);
    } else {
      toast.error('Profile not found');
    }
  };

  const validateAndNavigateToProfile = async (userId: string, userName: string = 'User') => {
    try {
      logger.log('🔍 [Validate Profile] Checking if profile exists:', userId);
      
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Please login to view profiles');
        return;
      }

      const response = await axios.get(`${API_URL}/profiles/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success && response.data.data) {
        logger.log('✅ [Validate Profile] Profile exists, navigating...');
        const profileData = response.data.data;
        const firstName = profileData.userId?.firstName || profileData.firstName || 'User';
        const lastName = profileData.userId?.lastName || profileData.lastName || '';
        const slug = generateProfileSlug(firstName, lastName, userId);
        navigate(`/profile/${slug}`);
      } else {
        logger.error('❌ [Validate Profile] Profile not found in response:', response.data);
        toast.error(`Profile not found for ${userName}`);
      }
    } catch (error: any) {
      logger.error('❌ [Validate Profile] Error checking profile:', {
        userId,
        error: error.message,
        status: error.response?.status,
        data: error.response?.data
      });
      
      if (error.response?.status === 404) {
        toast.error(`This user's profile is no longer available`);
      } else {
        toast.error('Unable to load profile. Please try again.');
      }
    }
  };

  const handleViewConversationProfile = async (conversation: any) => {
    logger.log('🔍 [View Profile] Starting profile view for conversation:', conversation?._id);
    
    if (conversation?._id === currentConversation?._id && currentOtherParticipant?._id) {
      logger.log('🔍 [View Profile] Using current conversation participant:', currentOtherParticipant._id);
      await validateAndNavigateToProfile(currentOtherParticipant._id, `${currentOtherParticipant.firstName} ${currentOtherParticipant.lastName}`);
      return;
    }
    
    let otherUser = getOtherParticipant(conversation);
    logger.log('🔍 [View Profile] Standard method result:', otherUser?._id);
    
    if (!otherUser?._id && conversation?._id) {
      const fullConversation = conversations.find(c => c._id === conversation._id);
      if (fullConversation) {
        otherUser = getOtherParticipant(fullConversation);
        logger.log('🔍 [View Profile] Full conversation method result:', otherUser?._id);
      }
    }
    
    if (!otherUser?._id && conversation) {
      if (conversation.participants && Array.isArray(conversation.participants)) {
        logger.log('🔍 [View Profile] Checking participants:', conversation.participants);
        otherUser = conversation.participants.find((p: any) => 
          (p._id || p) !== user?._id
        );
        if (otherUser?._id) logger.log('🔍 [View Profile] Found via participants array:', otherUser._id);
      }
      
      if (!otherUser?._id) {
        if (conversation.user1?._id && conversation.user1._id !== user?._id) {
          otherUser = conversation.user1;
          logger.log('🔍 [View Profile] Found via user1:', otherUser._id);
        } else if (conversation.user2?._id && conversation.user2._id !== user?._id) {
          otherUser = conversation.user2;
          logger.log('🔍 [View Profile] Found via user2:', otherUser._id);
        }
      }
      
      if (!otherUser?._id && conversation.lastMessage?.sender?._id && conversation.lastMessage.sender._id !== user?._id) {
        otherUser = conversation.lastMessage.sender;
        logger.log('🔍 [View Profile] Found via lastMessage sender:', otherUser._id);
      }
    }
    
    if (otherUser?._id) {
      logger.log('🔍 [View Profile] Final user found:', otherUser._id);
      await validateAndNavigateToProfile(otherUser._id, `${otherUser.firstName || ''} ${otherUser.lastName || ''}`.trim());
    } else {
      logger.error('❌ [View Profile] No user ID found:', {
        conversationId: conversation?._id,
        hasParticipants: !!conversation?.participants,
        conversation
      });
      toast.error('Cannot find user profile');
    }
  };

  // Handle clicking on a sent interest - create a virtual "pending" conversation
  const handleClickSentInterest = (interest: any) => {
    const profile = interest.toProfile || interest.toProfileId || {};
    const toUser = interest.toUserId || profile.userId || {};
    
    // Create a virtual conversation object for the sent interest
    const virtualConversation = {
      _id: `pending-${interest._id}`,
      participants: [
        {
          _id: toUser._id || 'unknown',
          firstName: toUser.firstName || profile.firstName || 'User',
          lastName: toUser.lastName || profile.lastName || '',
          profile: {
            profilePhoto: profile.photos?.[0]?.url || profile.photos?.[0] || profile.profilePhoto,
            age: profile.age,
            occupation: profile.occupation || profile.profession,
            location: profile.location
          }
        }
      ],
      lastMessage: {
        content: `⏳ Waiting for ${toUser.firstName || profile.firstName || 'user'} to accept your interest...`,
        createdAt: interest.createdAt,
        senderId: 'system',
        isWaiting: true
      },
      unreadCount: 0,
      type: 'pending',
      isPending: true,
      interestId: interest._id,
      interestMessage: interest.message || interest.metadata?.message
    };
    
    dispatch(setCurrentConversation(virtualConversation));
  };

  // Desktop & Mobile Layout
  return (
    <div className="flex h-full overflow-hidden bg-gray-50">
      {/* Left Sidebar - Conversation List */}
      <aside className={`${(currentConversation || selectedInterest) ? 'hidden md:flex' : 'flex'} w-full md:w-[380px] lg:w-[420px] flex-col border-r border-gray-200 bg-white shadow-sm`}>
        {/* Sidebar Header */}
        <div className="px-4 md:px-6 py-4 md:py-5 flex-none border-b border-gray-100">
          <h1 className="text-xl md:text-2xl font-bold text-gray-900 mb-1">Inbox</h1>
          <p className="text-xs md:text-sm text-gray-600">Manage your conversations and interests</p>
        </div>

        {/* Tabs */}
        <div className="flex-none px-4 md:px-6 pb-2 pt-4">
          <div className="flex border-b border-gray-200 gap-3 md:gap-6">
            <button
              onClick={() => setActiveTab('all')}
              className={`flex flex-col items-center justify-center border-b-[3px] pb-3 px-1 transition-colors flex-1 ${
                activeTab === 'all' 
                  ? 'border-rose-600 text-gray-900' 
                  : 'border-transparent text-gray-500 hover:text-rose-600'
              }`}
            >
              <p className="text-xs md:text-sm font-bold leading-normal">
                <span className="hidden sm:inline">All Messages</span>
                <span className="sm:hidden">All</span>
              </p>
            </button>
            <button
              onClick={() => setActiveTab('received')}
              className={`flex flex-col items-center justify-center border-b-[3px] pb-3 px-1 transition-colors flex-1 ${
                activeTab === 'received' 
                  ? 'border-rose-600 text-gray-900' 
                  : 'border-transparent text-gray-500 hover:text-rose-600'
              }`}
            >
              <p className="text-xs md:text-sm font-bold leading-normal">Received</p>
            </button>
            <button
              onClick={() => setActiveTab('sent')}
              className={`flex flex-col items-center justify-center border-b-[3px] pb-3 px-1 transition-colors flex-1 ${
                activeTab === 'sent' 
                  ? 'border-rose-600 text-gray-900' 
                  : 'border-transparent text-gray-500 hover:text-rose-600'
              }`}
            >
              <p className="text-xs md:text-sm font-bold leading-normal">Sent</p>
            </button>
          </div>
        </div>

        {/* Search */}
        <div className="flex-none px-4 md:px-6 py-3 md:py-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 md:h-5 md:w-5 text-gray-400" />
            <Input
              type="text"
              placeholder="Search conversations..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 md:pl-10 bg-gray-50 border-none focus:ring-1 focus:ring-rose-600 text-sm"
            />
          </div>
        </div>

        {/* Conversation List */}
        <div className="flex-1 overflow-y-auto px-3 pb-4 space-y-1">
          {/* Only show loading skeleton when truly loading from API, not when using cache */}
          {loading && !cacheValid && conversations.length === 0 ? (
            <div className="space-y-2 p-3">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="flex items-center gap-3 p-3 animate-pulse">
                  <div className="w-12 h-12 bg-gray-200 rounded-full" />
                  <div className="flex-1 space-y-2">
                    <div className="h-4 bg-gray-200 rounded w-3/4" />
                    <div className="h-3 bg-gray-100 rounded w-1/2" />
                  </div>
                </div>
              ))}
            </div>
          ) : activeTab === 'all' && filteredConversations.length === 0 ? (
            <div className="flex flex-col items-center justify-center p-8 text-center">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                <Search className="h-8 w-8 text-gray-400" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-1">No conversations yet</h3>
              <p className="text-sm text-gray-500">Start connecting with matches</p>
            </div>
          ) : activeTab === 'received' && receivedInterests.length === 0 ? (
            <div className="flex flex-col items-center justify-center p-8 text-center">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                <Heart className="h-8 w-8 text-gray-400" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-1">No interests received</h3>
              <p className="text-sm text-gray-500">When someone sends you an interest, it will appear here</p>
            </div>
          ) : activeTab === 'sent' && sentInterests.length === 0 ? (
            <div className="flex flex-col items-center justify-center p-8 text-center">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                <Send className="h-8 w-8 text-gray-400" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-1">No interests sent</h3>
              <p className="text-sm text-gray-500">Send interests to profiles you like to see them here</p>
            </div>
          ) : (
            <>
              {/* ALL MESSAGES TAB - Show accepted conversations */}
              {activeTab === 'all' && filteredConversations.map((conv: any) => {
                const otherParticipant = getOtherParticipant(conv);
                const isOnline = otherParticipant && onlineUsers.includes(otherParticipant._id);
                const isActive = currentConversation?._id === conv._id;
                const hasUnread = conv.unreadCount > 0;

                return (
                  <ConversationItem
                    key={conv._id}
                    conversation={conv}
                    otherParticipant={otherParticipant}
                    isOnline={isOnline}
                    isActive={isActive}
                    hasUnread={hasUnread}
                    onSelect={() => handleSelectConversation(conv)}
                    formatMessageTime={formatMessageTime}
                  />
                );
              })}

              {/* RECEIVED TAB - Show RECEIVED interests (with Accept/Decline) */}
              {activeTab === 'received' && receivedInterests.map((interest: any) => {
                const profile = interest.fromUserProfile || interest.fromProfile || {}; 
                const fromUser = interest.fromUser || interest.fromUserId || {};
                const userName = fromUser.firstName || profile.userId?.firstName || 'User';
                const userLastName = fromUser.lastName || profile.userId?.lastName || '';
                const userPhoto = profile.photos?.[0]?.url || profile.photos?.[0] || profile.profilePhoto || profile.userId?.profilePhoto;
                const isSelected = selectedInterest?._id === interest._id;

                return (
                  <div
                    key={interest._id}
                    onClick={() => setSelectedInterest(interest)}
                    className={`group flex items-center gap-2 sm:gap-3 p-2.5 sm:p-3 rounded-lg cursor-pointer transition-all ${
                      isSelected
                        ? 'bg-rose-50 border-l-4 border-rose-600'
                        : 'hover:bg-gray-50 border-l-4 border-transparent'
                    }`}
                  >
                    <div className="relative flex-shrink-0">
                      <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-full overflow-hidden">
                        {userPhoto ? (
                          <ImageWithFallback
                            src={userPhoto}
                            alt={`${userName} ${userLastName}`}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full bg-gradient-to-br from-rose-400 to-pink-500 flex items-center justify-center text-white font-semibold text-sm">
                            {userName?.charAt(0)}{userLastName?.charAt(0)}
                          </div>
                        )}
                      </div>
                      <span className="absolute bottom-0 right-0 block h-2.5 w-2.5 sm:h-3 sm:w-3 rounded-full bg-orange-400 ring-2 ring-white" />
                    </div>
                    <div className="flex flex-col flex-1 min-w-0">
                      <div className="flex justify-between items-baseline mb-0.5">
                        <p className="font-bold text-sm sm:text-base text-gray-900 truncate">
                          {userName} {userLastName?.charAt(0)}.
                        </p>
                        <Badge className="bg-orange-100 text-orange-700 border-orange-200 text-[10px] sm:text-xs px-1.5 sm:px-2 py-0.5 sm:py-1 shrink-0 ml-1">
                          Pending
                        </Badge>
                      </div>
                      <div className="flex justify-between items-center">
                        <p className="text-xs sm:text-sm truncate font-semibold text-gray-900">
                          Sent you an interest
                        </p>
                        <span className="h-1.5 w-1.5 sm:h-2 sm:w-2 bg-rose-600 rounded-full flex-shrink-0 ml-2" />
                      </div>
                    </div>
                  </div>
                );
              })}

              {/* SENT TAB - Show SENT interests (waiting for response) */}
              {activeTab === 'sent' && sentInterests.filter((interest: any) => {
                // Final safety check: only show if status is truly pending and no conversation exists
                return interest.status === 'pending' && !conversations.some((conv: any) => conv.interestId === interest._id);
              }).map((interest: any) => {
                const profile = interest.toProfile || interest.toProfileId || {};
                const userName = profile.userId?.firstName || profile.firstName || 'User';
                const userLastName = profile.userId?.lastName || profile.lastName || '';
                const userPhoto = profile.photos?.[0]?.url || profile.photos?.[0] || profile.profilePhoto || profile.userId?.profilePhoto;

                if (process.env.NODE_ENV === 'development') {
                  console.log('🎯 [SENT TAB] Rendering interest:', {
                    id: interest._id,
                    toProfile: interest.toProfile,
                    toProfileId: interest.toProfileId,
                    profile,
                    userName,
                    userLastName,
                    userPhoto,
                    fullInterest: interest
                  });
                }

                return (
                  <div
                    key={interest._id}
                    onClick={() => handleClickSentInterest(interest)}
                    className="group flex items-center gap-2 sm:gap-3 p-2.5 sm:p-3 rounded-lg hover:bg-gray-50 border-l-4 border-transparent cursor-pointer transition-all"
                  >
                    <div className="relative flex-shrink-0">
                      <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-full overflow-hidden">
                        {userPhoto ? (
                          <ImageWithFallback
                            src={userPhoto}
                            alt={`${userName} ${userLastName}`}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full bg-gradient-to-br from-rose-400 to-pink-500 flex items-center justify-center text-white font-semibold text-sm">
                            {userName?.charAt(0)}{userLastName?.charAt(0)}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-col flex-1 min-w-0">
                      <div className="flex justify-between items-baseline mb-0.5">
                        <p className="font-semibold text-sm sm:text-base text-gray-900 truncate">
                          {userName} {userLastName?.charAt(0)}.
                        </p>
                        <Badge className="bg-gray-100 text-gray-600 border-gray-200 text-[10px] sm:text-xs px-1.5 sm:px-2 py-0.5 sm:py-1 shrink-0 ml-1">
                          Sent
                        </Badge>
                      </div>
                      <div className="flex justify-between items-center gap-1">
                        <p className="text-xs sm:text-sm truncate text-gray-600">
                          {interest.message || interest.metadata?.message || 'Interest request sent'}
                        </p>
                        <p className="text-[10px] sm:text-xs text-gray-500 flex-shrink-0">
                          {formatMessageTime(interest.createdAt)}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </>
          )}
        </div>
      </aside>

      {/* Right Pane - Chat Area */}
      <main className={`${(currentConversation || selectedInterest) ? 'flex' : 'hidden md:flex'} flex-1 flex-col bg-white relative overflow-hidden`}>
        {selectedInterest ? (
          /* INTEREST VIEW */
          <>
            <div className="flex-none flex items-center justify-between px-3 sm:px-4 md:px-6 py-2.5 sm:py-3 border-b border-gray-200 bg-white shadow-sm">
              <div className="flex items-center gap-2 sm:gap-3 md:gap-4 flex-1 min-w-0">
                <button 
                  onClick={() => setSelectedInterest(null)} 
                  className="md:hidden p-1.5 sm:p-2 hover:bg-gray-100 rounded-full transition-colors shrink-0"
                >
                  <ArrowLeft className="h-4 w-4 sm:h-5 sm:w-5" />
                </button>
                {(() => { 
                  const profile = selectedInterest.fromUserProfile || selectedInterest.fromProfile || {}; 
                  const userInfo = selectedInterest.fromUser || selectedInterest.fromUserId || {}; 
                  const userName = userInfo.firstName || profile.userId?.firstName || profile.firstName || 'User'; 
                  const userLastName = userInfo.lastName || profile.userId?.lastName || profile.lastName || ''; 
                  const userPhoto = profile.photos?.[0]?.url || profile.photos?.[0] || profile.profilePhoto || profile.userId?.profilePhoto; 
                  return (
                    <>
                      <div className="w-9 h-9 sm:w-10 sm:h-10 md:w-12 md:h-12 rounded-full overflow-hidden shadow-sm shrink-0">
                        {userPhoto ? (
                          <ImageWithFallback 
                            src={userPhoto} 
                            alt={`${userName} ${userLastName}`} 
                            className="w-full h-full object-cover" 
                          />
                        ) : (
                          <div className="w-full h-full bg-gradient-to-br from-rose-400 to-pink-500 flex items-center justify-center text-white font-semibold text-xs sm:text-sm">
                            {userName?.charAt(0)}{userLastName?.charAt(0)}
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-sm sm:text-base md:text-lg font-bold text-gray-900 leading-tight truncate">
                          {userName} {userLastName?.charAt(0)}. 
                          <span className="hidden lg:inline text-xs sm:text-sm font-normal text-gray-500 ml-2">ID: M582910</span>
                        </h3>
                        <p className="text-[10px] sm:text-xs md:text-sm text-gray-600 truncate leading-tight">
                          {profile.age ? `${profile.age} yrs` : '26 yrs'}, {profile.height || `5'5"`}, {profile.occupation || 'Software Engineer'} • <span className="text-green-600 font-semibold">Online Now</span>
                        </p>
                      </div>
                    </>
                  ); 
                })()}
              </div>
              <Button 
                size="sm" 
                variant="outline" 
                className="text-rose-600 border-rose-200 hover:bg-rose-50 text-xs sm:text-sm shrink-0 ml-1 sm:ml-2 px-2 sm:px-3 h-8 sm:h-9" 
                onClick={() => handleViewInterestProfile(selectedInterest)}
              >
                <span className="hidden sm:inline">View Profile</span>
                <span className="sm:hidden">View</span>
              </Button>
            </div>
            <div className="flex-1 overflow-y-auto p-3 sm:p-4 md:p-6 space-y-4 md:space-y-6 bg-pink-50/30" style={{ minHeight: 0 }}>
              <div className="flex justify-center">
                <span className="text-xs md:text-sm font-medium text-gray-600 bg-white px-3 py-1 rounded-full shadow-sm">
                  Today
                </span>
              </div>
              <div className="flex justify-center w-full px-2 sm:px-4">
                <div className="bg-white border-2 border-rose-200 rounded-xl p-4 sm:p-5 md:p-6 shadow-md max-w-full sm:max-w-md md:max-w-lg w-full text-center relative overflow-hidden">
                  <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-rose-600 to-orange-500"></div>
                  <div className="h-12 w-12 sm:h-14 sm:w-14 md:h-16 md:w-16 bg-rose-100 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
                    <Heart className="h-6 w-6 sm:h-7 sm:w-7 md:h-8 md:w-8 text-rose-600 fill-rose-600" />
                  </div>
                  <h2 className="text-sm sm:text-base md:text-lg lg:text-xl text-gray-900 font-bold mb-2 sm:mb-3 px-2 leading-tight">
                    {(() => { 
                      const p = selectedInterest.fromUserProfile || selectedInterest.fromProfile || {}; 
                      const u = selectedInterest.fromUser || selectedInterest.fromUserId || {}; 
                      const n = u.firstName || p.userId?.firstName || p.firstName || 'User'; 
                      const l = u.lastName || p.userId?.lastName || p.lastName || ''; 
                      return `${n} ${l?.charAt(0)}. has sent you an Interest`; 
                    })()}
                  </h2>
                  <p className="text-gray-600 text-xs sm:text-sm md:text-base mb-4 sm:mb-5 md:mb-6 px-2 sm:px-4 md:px-6 leading-relaxed">
                    "{selectedInterest.message || selectedInterest.metadata?.message || 'Hi, I went through your profile and found it impressive. I would like to connect with you to know more.'}"
                  </p>
                  <div className="flex justify-center gap-2 sm:gap-3 md:gap-4 px-2">
                    <Button 
                      variant="outline" 
                      onClick={() => handleDeclineInterest(selectedInterest._id)} 
                      disabled={decliningInterest === selectedInterest._id} 
                      className="flex-1 max-w-[110px] sm:max-w-[130px] md:max-w-[150px] px-4 sm:px-5 md:px-6 py-2 sm:py-2.5 md:py-3 text-xs sm:text-sm md:text-base font-bold text-gray-700 border-2 border-gray-300 rounded-full hover:bg-gray-50 transition-all"
                    >
                      {decliningInterest === selectedInterest._id ? 'Declining...' : 'Decline'}
                    </Button>
                    <Button 
                      onClick={() => handleAcceptInterest(selectedInterest._id, selectedInterest)} 
                      disabled={acceptingInterest === selectedInterest._id} 
                      className="flex-1 max-w-[110px] sm:max-w-[130px] md:max-w-[150px] px-4 sm:px-5 md:px-6 py-2 sm:py-2.5 md:py-3 text-xs sm:text-sm md:text-base font-bold text-white bg-rose-600 rounded-full hover:bg-rose-700 shadow-lg shadow-rose-500/20 transition-all"
                    >
                      {acceptingInterest === selectedInterest._id ? 'Accepting...' : 'Accept'}
                    </Button>
                  </div>
                  <p className="text-[10px] sm:text-xs md:text-sm text-gray-600 mt-3 sm:mt-4 px-2">
                    Accepting will allow you to chat instantly.
                  </p>
                </div>
              </div>
            </div>
            {!authLoading && !hasActiveSubscription && (
              <div className="flex-none px-3 sm:px-4 md:px-6 pt-2 pb-2 md:pb-3">
                <div className="bg-gradient-to-r from-orange-50 to-red-50 border border-orange-200 rounded-lg md:rounded-xl p-3 md:p-4 flex flex-col md:flex-row items-start md:items-center justify-between gap-2 md:gap-3 shadow-sm relative overflow-hidden">
                  <div className="absolute -right-6 -top-6 h-24 w-24 bg-orange-500/10 rounded-full blur-2xl"></div>
                  <div className="flex items-start md:items-center gap-2 md:gap-4 relative z-10 flex-1 w-full md:w-auto">
                    <div className="h-9 w-9 md:h-10 md:w-10 bg-orange-100 rounded-full flex items-center justify-center text-orange-600 shrink-0 mt-0.5 md:mt-0">
                      <Mail className="h-4 w-4 md:h-5 md:w-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="text-gray-900 font-bold text-xs sm:text-sm md:text-base leading-tight">
                        You have <span className="text-rose-600">3 unread messages</span> waiting!
                      </h4>
                      <p className="text-gray-600 text-[10px] sm:text-xs md:text-sm mt-0.5 md:mt-1 leading-tight">
                        Upgrade your plan to unlock conversations.
                      </p>
                    </div>
                  </div>
                  <Button 
                    onClick={onNavigateToPlans} 
                    className="relative z-10 w-full md:w-auto px-4 md:px-5 py-2 md:py-2.5 bg-gradient-to-r from-rose-600 to-red-600 text-white font-bold text-xs sm:text-sm md:text-base rounded-lg shadow-md shrink-0"
                  >
                    View Plans
                  </Button>
                </div>
              </div>
            )}
            <div className="flex-none p-3 sm:p-4 md:p-4 bg-white border-t border-gray-200 relative">
              <div className="absolute inset-0 bg-white/60 backdrop-blur-[1px] z-10 flex items-center justify-center px-4">
                <p className="text-xs sm:text-sm md:text-base font-semibold text-gray-900 bg-white px-3 sm:px-4 md:px-5 py-1.5 sm:py-2 rounded-full shadow-sm border border-gray-200 text-center">
                  Accept interest to start chatting
                </p>
              </div>
              <div className="flex items-end gap-2 sm:gap-3 max-w-5xl mx-auto opacity-50">
                <div className="flex-1 bg-gray-50 border border-gray-200 rounded-xl flex items-center px-2 sm:px-3 py-2">
                  <textarea 
                    disabled 
                    className="w-full bg-transparent border-none text-gray-900 placeholder-gray-500 resize-none py-1 cursor-not-allowed text-xs sm:text-sm" 
                    placeholder="Type a message..." 
                    rows={1} 
                  />
                  <button disabled className="p-1.5 text-gray-500 cursor-not-allowed">
                    <Smile className="h-4 w-4 sm:h-5 sm:w-5" />
                  </button>
                </div>
                <button disabled className="h-9 w-9 sm:h-10 sm:w-10 flex items-center justify-center bg-gray-300 text-white rounded-full mb-1 cursor-not-allowed shrink-0">
                  <Send className="h-4 w-4 sm:h-5 sm:w-5 ml-0.5" />
                </button>
              </div>
            </div>
          </>
        ) : currentConversation ? (
          <>
            {/* Chat Header */}
            <div className="flex-none flex items-center justify-between px-3 sm:px-4 md:px-6 py-2.5 sm:py-3 border-b border-gray-200 bg-white shadow-sm">
              <div className="flex items-center gap-2 sm:gap-3 md:gap-4 flex-1 min-w-0">
                <button 
                  onClick={() => dispatch(setCurrentConversation(null))}
                  className="md:hidden p-2 hover:bg-gray-100 rounded-full transition-colors flex-shrink-0"
                >
                  <ArrowLeft className="h-5 w-5" />
                </button>
                <div className="w-10 h-10 sm:w-11 sm:h-11 md:w-12 md:h-12 rounded-full overflow-hidden shadow-sm flex-shrink-0">
                  {currentOtherParticipant?.profile?.profilePhoto ? (
                    <ImageWithFallback 
                      src={currentOtherParticipant.profile.profilePhoto} 
                      alt={`${currentOtherParticipant?.firstName} ${currentOtherParticipant?.lastName}`} 
                      className="w-full h-full object-cover" 
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-rose-400 to-pink-500 flex items-center justify-center text-white font-semibold text-sm">
                      {currentOtherParticipant?.firstName?.charAt(0)}{currentOtherParticipant?.lastName?.charAt(0)}
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 sm:gap-2">
                    <h3 className="text-sm sm:text-base md:text-lg font-bold text-gray-900 leading-tight truncate">
                      {currentOtherParticipant?.firstName} {currentOtherParticipant?.lastName?.charAt(0)}.
                      <span className="hidden sm:inline text-xs sm:text-sm font-normal text-gray-500 ml-1 sm:ml-2">
                        ID: {currentOtherParticipant?._id?.slice(-6)}
                      </span>
                    </h3>
                    {iBlockedThem && (
                      <Badge variant="outline" className="text-[10px] sm:text-xs px-1.5 sm:px-2 py-0.5 border-orange-300 bg-orange-50 text-orange-700 flex-shrink-0">
                        <Shield className="h-2.5 w-2.5 sm:h-3 sm:w-3 mr-0.5 sm:mr-1" />
                        <span className="hidden sm:inline">Blocked</span>
                        <span className="sm:hidden">!</span>
                      </Badge>
                    )}
                    {theyBlockedMe && (
                      <Badge variant="outline" className="text-[10px] sm:text-xs px-1.5 sm:px-2 py-0.5 border-red-300 bg-red-50 text-red-700 flex-shrink-0">
                        <Ban className="h-2.5 w-2.5 sm:h-3 sm:w-3 mr-0.5 sm:mr-1" />
                        <span className="hidden sm:inline">Blocked You</span>
                        <span className="sm:hidden">!</span>
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs sm:text-sm text-gray-600 truncate">
                    {currentOtherParticipant?.profile?.age && `${currentOtherParticipant.profile.age} yrs`}
                    {currentOtherParticipant?.profile?.height && `, ${currentOtherParticipant.profile.height}`}
                    <span className="hidden sm:inline">{currentOtherParticipant?.profile?.occupation && `, ${currentOtherParticipant.profile.occupation}`}</span>
                    {onlineUsers.includes(currentOtherParticipant?._id) && ' • Online Now'}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Button
                  size="sm"
                  onClick={handleInitiateVideoCall}
                  variant="ghost"
                  className="size-10 rounded-full p-0 text-gray-500 hover:bg-gray-100"
                  title="Video Call"
                >
                  <Video className="h-5 w-5" />
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="text-rose-600 border-rose-200 hover:bg-rose-50"
                  onClick={() => handleViewConversationProfile(currentConversation)}
                >
                  View Profile
                </Button>
                <DropdownMenu>
                  <DropdownMenuTrigger className="text-gray-600 hover:bg-gray-100 rounded-full p-2">
                    <MoreVertical className="h-5 w-5" />
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="w-56">
                    <div className="px-2 py-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Video className="h-4 w-4 text-gray-600" />
                          <span className="text-sm">Enable Video Calls</span>
                        </div>
                        <Switch
                          checked={videoCallEnabled}
                          onCheckedChange={handleToggleVideoCallPermission}
                          disabled={isUpdatingVideoCallPref}
                        />
                      </div>
                      <p className="text-xs text-gray-500 mt-1 ml-6">
                        {videoCallEnabled ? 'You can receive video calls' : 'Video calls are disabled'}
                      </p>
                    </div>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={() => handleReportUser(
                        currentOtherParticipant?._id || '', 
                        currentOtherParticipant?.firstName || 'this user'
                      )}
                      disabled={reportingUser}
                    >
                      <Flag className="mr-2 h-4 w-4" />
                      Report User
                    </DropdownMenuItem>
                    {iBlockedThem ? (
                      <DropdownMenuItem
                        onClick={handleUnblock}
                        disabled={unblocking}
                      >
                        <Ban className="mr-2 h-4 w-4" />
                        {unblocking ? 'Unblocking...' : 'Unblock User'}
                      </DropdownMenuItem>
                    ) : (
                      <DropdownMenuItem
                        onClick={() => handleBlockUser(currentOtherParticipant?._id || '')}
                        disabled={blockingUser}
                      >
                        <Ban className="mr-2 h-4 w-4" />
                        {blockingUser ? 'Blocking...' : 'Block User'}
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={() => handleViewConversationProfile(currentConversation)}
                    >
                      <User className="mr-2 h-4 w-4" />
                      View Profile
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>

            {/* Messages Stream */}
            <div 
              ref={messagesContainerRef}
              id="messages-stream"
              className="flex-1 overflow-y-auto overflow-x-hidden p-3 sm:p-6 space-y-4 sm:space-y-6 bg-pink-50/30"
              style={{ minHeight: 0 }}
              onScroll={handleScroll}
            >
              {/* SENT INTEREST PENDING VIEW */}
              {currentConversation?.isPending || currentConversation?.type === 'pending' ? (
                <div className="flex items-center justify-center h-full">
                  <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-8 text-center border-2 border-amber-200">
                    <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Send className="w-8 h-8 text-amber-600" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">
                      Waiting for {getOtherParticipant(currentConversation)?.firstName} to Accept
                    </h3>
                    <p className="text-gray-600 mb-6">
                      You've sent an interest to {getOtherParticipant(currentConversation)?.firstName}. Once they accept, you'll be able to start chatting!
                    </p>
                    {currentConversation?.interestMessage && (
                      <div className="bg-gray-50 rounded-lg p-4 mb-6">
                        <p className="text-sm text-gray-700 italic">
                          "{currentConversation.interestMessage}"
                        </p>
                        <p className="text-xs text-gray-500 mt-2">Your message</p>
                      </div>
                    )}
                    <div className="flex items-center justify-center gap-2 text-sm text-amber-700 bg-amber-50 py-3 px-4 rounded-lg">
                      <div className="w-2 h-2 bg-amber-500 rounded-full animate-pulse"></div>
                      <span>Interest pending...</span>
                    </div>
                  </div>
                </div>
              ) : messagesLoading ? (
                <div className="space-y-4">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="flex gap-3">
                      <div className="w-8 h-8 rounded-full bg-gray-200 animate-pulse" />
                      <div className="flex-1 space-y-2">
                        <div className="h-16 bg-gray-200 animate-pulse rounded-2xl w-3/4" />
                        <div className="h-3 bg-gray-100 animate-pulse w-20 rounded" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : messages.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-center">
                  {/* Interest Request Card - Show ONLY if conversation has pending interest AND I am the recipient */}
                  {currentConversation?.interestStatus === 'pending' && currentConversation?.interestRecipient === user?._id ? (
                    <div className="flex justify-center w-full mb-6">
                      <div className="bg-white border border-rose-200 rounded-xl p-6 shadow-md max-w-md w-full text-center">
                        <div className="h-12 w-12 bg-rose-100 rounded-full flex items-center justify-center mx-auto mb-3">
                          <Heart className="h-6 w-6 text-rose-600 fill-rose-600" />
                        </div>
                        <p className="text-gray-900 font-bold text-lg mb-2">
                          {getOtherParticipant(currentConversation)?.firstName} {getOtherParticipant(currentConversation)?.lastName?.charAt(0)}. sent you an interest request
                        </p>
                        <p className="text-gray-600 text-sm mb-6">
                          "{currentConversation?.interestMessage || 'Hi, I liked your profile and would like to connect with you.'}"
                        </p>
                        <div className="flex justify-center gap-3">
                          <Button
                            onClick={() => handleDeclineInterest(currentConversation._id)}
                            disabled={decliningInterest === currentConversation._id}
                            variant="outline"
                            className="px-8 py-2 text-gray-700 border-gray-300 hover:bg-gray-50 rounded-full font-semibold"
                          >
                            {decliningInterest === currentConversation._id ? 'Declining...' : 'Decline'}
                          </Button>
                          <Button
                            onClick={() => {
                              handleAcceptInterest(currentConversation._id, currentConversation);
                            }}
                            disabled={acceptingInterest === currentConversation._id}
                            className="px-8 py-2 bg-rose-600 hover:bg-rose-700 text-white rounded-full shadow-md font-semibold"
                          >
                            {acceptingInterest === currentConversation._id ? 'Accepting...' : 'Accept Interest'}
                          </Button>
                        </div>
                        
                        {/* Plan upgrade message if user doesn't have plan */}
                        {!authLoading && !hasActiveSubscription && (
                          <div className="mt-6 p-4 bg-gradient-to-r from-rose-50 to-pink-50 border border-rose-200 rounded-lg">
                            <div className="flex items-center gap-3 mb-3">
                              <Lock className="h-5 w-5 text-rose-600" />
                              <p className="text-sm font-semibold text-gray-900">Premium Feature Required</p>
                            </div>
                            <p className="text-xs text-gray-600 mb-3">
                              Upgrade to a premium plan to accept interest requests and start conversations.
                            </p>
                            <Button
                              onClick={onNavigateToPlans}
                              size="sm"
                              className="w-full bg-gradient-to-r from-rose-600 to-pink-600 hover:from-rose-700 hover:to-pink-700 text-white font-semibold"
                            >
                              <CreditCard className="h-4 w-4 mr-2" />
                              View Plans & Upgrade
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  ) : currentConversation?.interestStatus === 'pending' && currentConversation?.interestSender === user?._id ? (
                    <div className="flex justify-center w-full mb-6">
                      <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-md max-w-md w-full text-center">
                        <div className="h-12 w-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                          <Send className="h-6 w-6 text-gray-600" />
                        </div>
                        <p className="text-gray-900 font-bold text-lg mb-2">
                          Interest Sent
                        </p>
                        <p className="text-gray-600 text-sm mb-4">
                          You sent an interest to {getOtherParticipant(currentConversation)?.firstName} {getOtherParticipant(currentConversation)?.lastName?.charAt(0)}.
                        </p>
                        <p className="text-gray-500 text-xs mb-6">
                          Your message: "{currentConversation?.interestMessage || 'Hi, I liked your profile and would like to connect with you.'}"
                        </p>
                        <div className="flex items-center justify-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                          <div className="h-8 w-8 bg-amber-100 rounded-full flex items-center justify-center">
                            <svg className="h-5 w-5 text-amber-600 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                          </div>
                          <div className="text-left">
                            <p className="text-xs font-semibold text-amber-900">Waiting for Response</p>
                            <p className="text-xs text-amber-700">They will be notified of your interest</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="bg-white rounded-full p-6 mb-4 shadow-sm">
                        <Send className="h-12 w-12 text-gray-400" />
                      </div>
                      <h3 className="text-lg font-semibold text-gray-700 mb-2">No messages yet</h3>
                      <p className="text-sm text-gray-500 max-w-xs">
                        Start the conversation with {getOtherParticipant(currentConversation)?.firstName}
                      </p>
                    </>
                  )}
                </div>
              ) : (
                <>
                  {/* Date Separator */}
                  <div className="flex justify-center">
                    <span className="text-xs font-medium text-gray-600 bg-white px-3 py-1 rounded-full shadow-sm">
                      {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                    </span>
                  </div>

                  {/* Blocked Status Card - Show if user is blocked */}
                  {theyBlockedMe && (
                    <div className="flex justify-center w-full my-4">
                      <div className="bg-gradient-to-br from-red-50 to-rose-50 border-2 border-red-200 rounded-xl p-6 shadow-md max-w-md w-full text-center relative overflow-hidden">
                        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-red-500 to-rose-500"></div>
                        <div className="h-14 w-14 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-3">
                          <Ban className="h-7 w-7 text-red-600" />
                        </div>
                        <h2 className="text-lg text-gray-900 font-bold mb-2">
                          {currentOtherParticipant?.firstName} {currentOtherParticipant?.lastName?.charAt(0)}. has blocked you
                        </h2>
                        <p className="text-gray-600 text-sm mb-3">
                          You cannot send messages or view recent activities until they unblock you.
                        </p>
                        <div className="bg-white/80 border border-red-200 rounded-lg p-3 mt-4">
                          <p className="text-xs text-gray-600">
                            🛡️ If you believe this is a mistake or want to report abuse, please contact our support team.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {iBlockedThem && (
                    <div className="flex justify-center w-full my-4">
                      <div className="bg-gradient-to-br from-orange-50 to-amber-50 border-2 border-orange-200 rounded-xl p-6 shadow-md max-w-md w-full text-center relative overflow-hidden">
                        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-orange-500 to-amber-500"></div>
                        <div className="h-14 w-14 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-3">
                          <Shield className="h-7 w-7 text-orange-600" />
                        </div>
                        <h2 className="text-lg text-gray-900 font-bold mb-2">
                          You blocked {currentOtherParticipant?.firstName} {currentOtherParticipant?.lastName?.charAt(0)}.
                        </h2>
                        <p className="text-gray-600 text-sm mb-4">
                          You won't receive messages from this user. Unblock to restore communication.
                        </p>
                        <Button
                          onClick={handleUnblock}
                          disabled={unblocking}
                          size="sm"
                          className="px-8 py-2.5 text-sm font-bold text-white bg-gradient-to-r from-orange-600 to-amber-600 rounded-full hover:from-orange-700 hover:to-amber-700 shadow-lg shadow-orange-500/20 transform hover:scale-105 transition-all"
                        >
                          {unblocking ? 'Unblocking...' : 'Unblock User'}
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Interest Request Card - Show at top of messages ONLY if pending AND I am the recipient */}
                  {currentConversation?.interestStatus === 'pending' && currentConversation?.interestRecipient === user?._id && (
                    <div className="flex justify-center w-full">
                      <div className="bg-white border border-rose-200 rounded-xl p-4 shadow-sm max-w-md w-full text-center">
                        <div className="h-10 w-10 bg-rose-100 rounded-full flex items-center justify-center mx-auto mb-2">
                          <Heart className="h-5 w-5 text-rose-600" />
                        </div>
                        <p className="text-gray-900 font-semibold mb-1">
                          {getOtherParticipant(currentConversation)?.firstName} sent you an interest request
                        </p>
                        <p className="text-gray-600 text-xs mb-4">
                          "{currentConversation?.interestMessage || 'Hi, I liked your profile and would like to connect with you.'}"
                        </p>
                        <div className="flex justify-center gap-3">
                          <Button
                            onClick={() => handleDeclineInterest(currentConversation._id)}
                            disabled={decliningInterest === currentConversation._id}
                            size="sm"
                            variant="outline"
                            className="px-6 text-gray-700 border-gray-300 hover:bg-gray-50 rounded-full"
                          >
                            Decline
                          </Button>
                          <Button
                            onClick={() => {
                              handleAcceptInterest(currentConversation._id, currentConversation);
                            }}
                            disabled={acceptingInterest === currentConversation._id}
                            size="sm"
                            className="px-6 bg-rose-600 hover:bg-rose-700 text-white rounded-full shadow-sm"
                          >
                            Accept Interest
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Load More Button / Indicator */}
                  {hasMoreMessages && messages.length > 0 && (
                    <div className="flex justify-center py-4">
                      {loadingOlderMessages ? (
                        <div className="flex items-center gap-2 text-gray-500 text-sm">
                          <div className="h-4 w-4 border-2 border-gray-300 border-t-rose-600 rounded-full animate-spin" />
                          <span>Loading older messages...</span>
                        </div>
                      ) : (
                        <button
                          onClick={handleLoadMore}
                          className="px-4 py-2 text-sm text-rose-600 hover:text-rose-700 hover:bg-rose-50 rounded-full font-semibold transition-colors"
                        >
                          Load older messages
                        </button>
                      )}
                    </div>
                  )}
                  
                  {!hasMoreMessages && messages.length > 0 && (
                    <div className="flex justify-center py-2">
                      <p className="text-xs text-gray-400 italic">Beginning of conversation</p>
                    </div>
                  )}

                  {/* System Message for Accepted Interest */}
                  {currentConversation?.interestStatus === 'accepted' && (
                    <div className="flex justify-center">
                      <p className="text-xs text-gray-500 italic">
                        You accepted this interest request on {new Date(currentConversation?.interestAcceptedAt || Date.now()).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}.
                      </p>
                    </div>
                  )}

                  {messages.map((message: any) => {
                    const isOwnMessage = message.senderId?._id === user?._id || message.senderId === user?._id;

                    return (
                      <div
                        key={message._id}
                        className={`flex items-end gap-2 w-full ${isOwnMessage ? 'flex-row-reverse' : 'flex-row'}`}
                      >
                        {!isOwnMessage && (
                          <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full overflow-hidden flex-shrink-0 mb-1">
                            {getOtherParticipant(currentConversation)?.profile?.profilePhoto ? (
                              <ImageWithFallback
                                src={getOtherParticipant(currentConversation).profile.profilePhoto}
                                alt={`${getOtherParticipant(currentConversation)?.firstName}`}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="w-full h-full bg-gradient-to-br from-rose-400 to-pink-500 flex items-center justify-center text-white text-xs font-semibold">
                                {getOtherParticipant(currentConversation)?.firstName?.charAt(0)}
                              </div>
                            )}
                          </div>
                        )}
                        <div className={`flex flex-col gap-1 min-w-0 max-w-[calc(100%-3rem)] sm:max-w-[75%] md:max-w-[70%] ${isOwnMessage ? 'items-end' : 'items-start'}`}>
                          <div className={`chat-message-bubble p-2.5 sm:p-3 rounded-2xl shadow-sm ${
                            isOwnMessage 
                              ? 'bg-rose-600 text-white rounded-br-none' 
                              : 'bg-white border border-gray-200 text-gray-900 rounded-bl-none'
                          }`}>
                            <p className="text-sm leading-relaxed">{message.content}</p>
                          </div>
                          <div className="flex items-center gap-1 px-1">
                            <span className="text-[11px] text-gray-500">
                              {formatFullMessageTime(message.createdAt)}
                            </span>
                            {isOwnMessage && (
                              <CheckCircle2 className="h-3.5 w-3.5 text-rose-600" />
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  
                  {/* Scroll anchor - always at the bottom */}
                  <div ref={messagesEndRef} />
                </>
              )}
            </div>

            {/* Upgrade Banner - Show above composer if user doesn't have plan */}
            {!authLoading && !hasActiveSubscription && (
              <div className="flex-none px-4 pt-2 pb-0">
                <div className="bg-gradient-to-r from-orange-50 to-red-50 border border-orange-200 rounded-xl p-4 flex items-center justify-between shadow-sm relative overflow-hidden">
                  <div className="absolute -right-6 -top-6 h-24 w-24 bg-orange-500/10 rounded-full blur-2xl"></div>
                  <div className="flex items-center gap-4 relative z-10">
                    <div className="h-10 w-10 bg-orange-100 rounded-full flex items-center justify-center text-orange-600 shrink-0">
                      <Mail className="h-5 w-5" />
                    </div>
                    <div>
                      <h4 className="text-gray-900 font-bold text-sm">
                        You have <span className="text-rose-600">unread messages</span> waiting!
                      </h4>
                      <p className="text-gray-600 text-xs mt-0.5">
                        Upgrade your plan to unlock conversations and reply instantly.
                      </p>
                    </div>
                  </div>
                  <Button
                    onClick={onNavigateToPlans}
                    className="relative z-10 px-5 py-2 bg-gradient-to-r from-rose-600 to-red-600 hover:from-rose-700 hover:to-red-700 text-white font-bold rounded-lg shadow-md"
                  >
                    View Plans
                  </Button>
                </div>
              </div>
            )}

            {/* Composer Area */}
            <div className="flex-none p-2 sm:p-3 md:p-4 bg-white border-t border-gray-200">
              {iBlockedThem ? (
                /* User blocked this person */
                <div className="max-w-5xl mx-auto">
                  <div className="bg-gradient-to-br from-orange-50 to-amber-50 border-2 border-orange-200 rounded-xl p-6 text-center shadow-md relative overflow-hidden">
                    <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-orange-500 to-amber-500"></div>
                    <div className="h-12 w-12 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-3">
                      <Shield className="h-6 w-6 text-orange-600" />
                    </div>
                    <h3 className="text-base font-bold text-gray-900 mb-2">
                      You blocked this user
                    </h3>
                    <p className="text-gray-600 text-sm mb-4">
                      Unblock to send messages and continue the conversation.
                    </p>
                    <Button
                      onClick={handleUnblock}
                      disabled={unblocking}
                      size="sm"
                      className="px-8 py-2.5 text-sm font-bold text-white bg-gradient-to-r from-orange-600 to-amber-600 rounded-full hover:from-orange-700 hover:to-amber-700 shadow-lg shadow-orange-500/20 transform hover:scale-105 transition-all"
                    >
                      {unblocking ? 'Unblocking...' : 'Unblock User'}
                    </Button>
                  </div>
                </div>
              ) : theyBlockedMe ? (
                /* This person blocked the user */
                <div className="max-w-5xl mx-auto">
                  <div className="bg-gradient-to-br from-red-50 to-rose-50 border-2 border-red-200 rounded-xl p-6 text-center shadow-md relative overflow-hidden">
                    <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-red-500 to-rose-500"></div>
                    <div className="h-12 w-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-3">
                      <Ban className="h-6 w-6 text-red-600" />
                    </div>
                    <h3 className="text-base font-bold text-gray-900 mb-2">
                      This user has blocked you
                    </h3>
                    <p className="text-gray-600 text-sm mb-2">
                      You cannot send messages until they unblock you.
                    </p>
                    <p className="text-xs text-gray-500">
                      If you believe this is a mistake, you can contact support.
                    </p>
                  </div>
                </div>
              ) : currentConversation?.isPending || currentConversation?.type === 'pending' ? (
                /* Disabled composer for pending interests */
                <div className="flex items-end gap-2 sm:gap-3 max-w-5xl mx-auto opacity-50">
                  <button disabled className="hidden sm:block p-2 text-gray-500 mb-1 cursor-not-allowed">
                    <Plus className="h-5 w-5" />
                  </button>
                  <div className="flex-1 bg-gray-50 border border-gray-200 rounded-xl flex items-center px-2 sm:px-3 py-1.5 sm:py-2">
                    <textarea
                      disabled
                      className="w-full bg-transparent border-none text-gray-900 placeholder-gray-500 resize-none py-1 cursor-not-allowed text-sm"
                      placeholder="Waiting for acceptance..."
                      rows={1}
                    />
                    <button disabled className="p-1 sm:p-1.5 text-gray-500 cursor-not-allowed">
                      <Smile className="h-4 w-4 sm:h-5 sm:w-5" />
                    </button>
                  </div>
                  <button 
                    disabled
                    className="h-9 w-9 sm:h-10 sm:w-10 flex items-center justify-center bg-gray-300 text-white rounded-full mb-1 cursor-not-allowed flex-shrink-0"
                  >
                    <Send className="h-4 w-4 sm:h-5 sm:w-5 ml-0.5" />
                  </button>
                </div>
              ) : !authLoading && !hasActiveSubscription ? (
                /* Disabled composer for users without plan */
                <div className="flex items-end gap-2 sm:gap-3 max-w-5xl mx-auto opacity-60 relative">
                  <button disabled className="hidden sm:block p-2 text-gray-500 mb-1 cursor-not-allowed">
                    <Plus className="h-5 w-5" />
                  </button>
                  <div className="flex-1 bg-gray-50 border border-gray-300 rounded-xl flex items-center px-2 sm:px-3 py-1.5 sm:py-2 relative">
                    <textarea
                      disabled
                      className="w-full bg-transparent border-none text-gray-600 placeholder-gray-400 resize-none py-1 cursor-not-allowed text-sm"
                      placeholder="Upgrade to send messages..."
                      rows={1}
                    />
                    <button disabled className="p-1 sm:p-1.5 text-gray-500 cursor-not-allowed">
                      <Smile className="h-4 w-4 sm:h-5 sm:w-5" />
                    </button>
                  </div>
                  <button 
                    disabled
                    className="h-9 w-9 sm:h-10 sm:w-10 flex items-center justify-center bg-gray-300 text-white rounded-full mb-1 cursor-not-allowed flex-shrink-0"
                  >
                    <Send className="h-4 w-4 sm:h-5 sm:w-5 ml-0.5" />
                  </button>
                </div>
              ) : (
                /* Normal active composer */
                <div className="flex items-end gap-2 sm:gap-3 max-w-5xl mx-auto relative">
                  <div className="flex-1 bg-gray-50 border border-gray-200 rounded-xl flex items-center px-2 sm:px-3 py-1.5 sm:py-2 focus-within:ring-2 focus-within:ring-rose-600/20 focus-within:border-rose-600 transition-all">
                    <textarea
                      value={messageInput}
                      onChange={(e) => setMessageInput(e.target.value)}
                      onKeyPress={handleKeyPress}
                      className="w-full bg-transparent border-none focus:ring-0 text-gray-900 placeholder-gray-500 resize-none py-1 max-h-32 focus:outline-none text-sm sm:text-base"
                      placeholder="Type a message..."
                      rows={1}
                    />
                    <div className="relative">
                      <button 
                        onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                        className="p-1 sm:p-1.5 text-gray-500 hover:text-rose-600 transition-colors rounded-full hover:bg-gray-100"
                      >
                        <Smile className="h-4 w-4 sm:h-5 sm:w-5" />
                      </button>
                      
                      {/* Emoji Picker Popup */}
                      {showEmojiPicker && (
                        <>
                          {/* Backdrop to close emoji picker */}
                          <div 
                            className="fixed inset-0 z-40" 
                            onClick={() => setShowEmojiPicker(false)}
                          />
                          <div className="absolute bottom-full right-0 mb-2 bg-white rounded-lg shadow-xl border border-gray-200 p-3 z-50 w-64 sm:w-80">
                            <div className="flex items-center justify-between mb-2">
                              <h4 className="text-xs font-semibold text-gray-700">Quick Emojis</h4>
                              <button 
                                onClick={() => setShowEmojiPicker(false)}
                                className="text-gray-400 hover:text-gray-600"
                              >
                                ✕
                              </button>
                            </div>
                            <div className="grid grid-cols-5 sm:grid-cols-7 gap-2">
                              {commonEmojis.map((emoji, index) => (
                                <button
                                  key={index}
                                  onClick={() => handleEmojiSelect(emoji)}
                                  className="text-2xl hover:bg-gray-100 rounded p-2 transition-colors active:scale-95"
                                >
                                  {emoji}
                                </button>
                              ))}
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                  <button 
                    onClick={handleSendMessage}
                    disabled={!messageInput.trim()}
                    className="h-9 w-9 sm:h-10 sm:w-10 flex items-center justify-center bg-rose-600 text-white rounded-full hover:bg-rose-700 shadow-md transition-all mb-1 disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
                  >
                    <Send className="h-4 w-4 sm:h-5 sm:w-5 ml-0.5" />
                  </button>
                </div>
              )}
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center bg-gray-50">
            <div className="text-center">
              <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Search className="h-10 w-10 text-gray-400" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Select a conversation</h3>
              <p className="text-gray-600">Choose a conversation from the list to start messaging</p>
            </div>
          </div>
        )}
      </main>
      
      {/* Report User Modal */}
      <ReportUserModal
        isOpen={isReportModalOpen}
        onClose={() => {
          setIsReportModalOpen(false);
          setUserToReport(null);
        }}
        onSubmit={handleSubmitReport}
        userName={userToReport?.name}
      />
    </div>
  );
}

export default MessagesTabBeautiful;